/* eslint-disable no-unused-vars */
import React from 'react';
import { Button, useToast } from '@chakra-ui/react';
import { MagnifyingGlass } from 'phosphor-react';
import { useMutation } from '@tanstack/react-query';
import { ICnpjConsultaResponse, useSocietario } from '../../../../../hooks/useSocietario';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';

interface CnpjLookupButtonProps {
  // Lê o CNPJ atual do form (com ou sem máscara) no momento do clique.
  getCnpj: () => string | null | undefined;
  // Recebe os dados públicos pra o pai espalhar nos campos do sócio.
  onFound: (dados: ICnpjConsultaResponse) => void;
}

// Botão "Buscar dados" ao lado do CNPJ do sócio PJ. Espelha o
// DocumentoUploader (CNH/RG → PF): consulta pública (BrasilAPI + CNPJA)
// pré-preenche a "casca" da PJ (razão social, sede, telefone, representante,
// UF da junta). O CPF do representante vem mascarado nas bases públicas —
// fica pro operador; idem RG/profissão/estado civil/nascimento/endereço
// pessoal (exigidos só na constituição), que não existem em base de CNPJ.
export const CnpjLookupButton = ({ getCnpj, onFound }: CnpjLookupButtonProps) => {
  const { consultarCnpj } = useSocietario();
  const toast = useToast();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: consultarCnpj,
    onSuccess: (dados: ICnpjConsultaResponse) => {
      onFound(dados);
      toast({
        status: 'success',
        title: 'Dados da empresa preenchidos',
        description: dados.representante_cpf
          ? undefined
          : 'Confira o representante e informe o CPF dele (não vem nas bases públicas).',
      });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const message = extractApiErrorMessage(
        err,
        'Não foi possível consultar o CNPJ. Tente novamente.',
      );
      toast({
        status: status === 404 ? 'warning' : 'error',
        title: status === 404 ? 'CNPJ não encontrado' : 'Falha na consulta do CNPJ',
        description: message,
      });
    },
  });

  const handleClick = () => {
    const cnpjLimpo = (getCnpj() || '').replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      toast({
        status: 'warning',
        title: 'Informe um CNPJ válido',
        description: 'Digite os 14 dígitos do CNPJ antes de buscar.',
      });
      return;
    }
    mutateAsync(cnpjLimpo).catch(() => {
      // erro já tratado no onError; catch evita unhandled rejection.
    });
  };

  return (
    <Button
      leftIcon={<MagnifyingGlass size={16} />}
      onClick={handleClick}
      isLoading={isPending}
      loadingText="Buscando..."
      colorScheme="blue"
      variant="outline"
      size="sm"
      mt={1}
    >
      Buscar dados do CNPJ
    </Button>
  );
};
